import Decimal from 'decimal.js';
import { buildMetricValueCsv } from './csvExport';
import type { LedgerEvent } from './types';

const AMT_EPS = new Decimal('1e-10');

/** Max delay between outbound bridge leg and inbound receipt (strict; audit-friendly). */
const MAX_BRIDGE_LEG_GAP_MS = 14 * 24 * 60 * 60 * 1000;

export const CRONOS_BRIDGE_FI_SUBREASON = 'CRONOS_POS_EVM_BRIDGE_CONTINUITY' as const;

export type CronosBridgeContinuityStats = {
  /** Matched outbound + inbound legs (same bridge contract, amount, time window). */
  pairsMatched: number;
  disposalLegsRemoved: number;
  acquisitionLegsRemoved: number;
  /** W1 → W2 synthetic internal rows (distinct wallets). */
  syntheticInternalInserted: number;
  /** Both legs removed; no synthetic (same wallet on both sides). */
  sameWalletPairsSuppressed: number;
  /** Sum of CRO amount across matched pairs. */
  croAmountBridged: Decimal;
  /** Disposal to a configured bridge contract with no matching inbound leg. */
  unpairedDisposalToBridge: number;
  /** Inbound from bridge with no matching outbound leg. */
  unpairedAcquisitionFromBridge: number;
  /** Rows tagged `cronos_bridge_unpaired_disposal` / `cronos_bridge_unpaired_acquisition`. */
  reviewFlagsAdded: number;
};

function normAsset(a: string): string {
  const u = a.trim().toUpperCase();
  return u === 'WCRO' ? 'CRO' : u;
}

function amountsMatch(a: Decimal, b: Decimal): boolean {
  if (a.sub(b).abs().lte(AMT_EPS)) return true;
  const max = Decimal.max(a.abs(), b.abs());
  if (max.lte(0)) return false;
  return a.sub(b).abs().div(max).lte(new Decimal('1e-6'));
}

function normAddr(a: string | undefined): string | null {
  const t = String(a || '').trim().toLowerCase();
  return t.startsWith('0x') && t.length === 42 ? t : null;
}

function isCroLike(ev: LedgerEvent): boolean {
  return normAsset(ev.asset) === 'CRO';
}

export function isCronosBridgeDisposalCandidate(
  ev: LedgerEvent,
  owned: Set<string>,
  bridge: Set<string>
): boolean {
  if (ev.source !== 'cronos') return false;
  if (ev.kind !== 'disposal') return false;
  if (ev.classification !== 'TRANSFER_OUT') return false;
  if (!isCroLike(ev)) return false;
  const f = normAddr(ev.fromAddress);
  const t = normAddr(ev.toAddress);
  if (!f || !t) return false;
  if (!owned.has(f)) return false;
  if (owned.has(t)) return false;
  return bridge.has(t);
}

export function isCronosBridgeAcquisitionCandidate(
  ev: LedgerEvent,
  owned: Set<string>,
  bridge: Set<string>
): boolean {
  if (ev.source !== 'cronos') return false;
  if (ev.kind !== 'acquisition') return false;
  if (ev.classification !== 'TRANSFER_IN') return false;
  if (!isCroLike(ev)) return false;
  const f = normAddr(ev.fromAddress);
  const t = normAddr(ev.toAddress);
  if (!f || !t) return false;
  if (!owned.has(t)) return false;
  if (owned.has(f)) return false;
  return bridge.has(f);
}

function withReviewFlag(ev: LedgerEvent, flag: string): LedgerEvent {
  const prev = ev.reviewFlags ?? [];
  if (prev.includes(flag)) return ev;
  return { ...ev, reviewFlags: [...prev, flag] };
}

type Match = { disposal: LedgerEvent; acquisition: LedgerEvent };

/**
 * Pairs Cronos CRO bridge legs (strict): owned → bridge (disposal) with later bridge → owned
 * (acquisition), same bridge address on counterparty, amount match, time window.
 * Replaces the pair with a non-taxable internal continuity row when wallets differ; removes both
 * only when source and destination wallet are the same.
 */
export function applyCronosBridgeMigrationContinuity(
  events: LedgerEvent[],
  options: {
    ownedAddresses: Set<string>;
    bridgeContracts: Set<string>;
  }
): { events: LedgerEvent[]; stats: CronosBridgeContinuityStats } {
  const owned = options.ownedAddresses;
  const bridge = options.bridgeContracts;

  const emptyStats = (): CronosBridgeContinuityStats => ({
    pairsMatched: 0,
    disposalLegsRemoved: 0,
    acquisitionLegsRemoved: 0,
    syntheticInternalInserted: 0,
    sameWalletPairsSuppressed: 0,
    croAmountBridged: new Decimal(0),
    unpairedDisposalToBridge: 0,
    unpairedAcquisitionFromBridge: 0,
    reviewFlagsAdded: 0,
  });

  if (owned.size === 0 || bridge.size === 0) {
    return { events, stats: emptyStats() };
  }

  const disposals = events.filter((e) => isCronosBridgeDisposalCandidate(e, owned, bridge));
  const acquisitions = events.filter((e) => isCronosBridgeAcquisitionCandidate(e, owned, bridge));

  if (disposals.length === 0 || acquisitions.length === 0) {
    const stats = emptyStats();
    let out = events;
    if (disposals.length > 0) {
      stats.unpairedDisposalToBridge = disposals.length;
      const dset = new Set(disposals);
      out = events.map((e) => {
        if (!dset.has(e)) return e;
        stats.reviewFlagsAdded++;
        return withReviewFlag(e, 'cronos_bridge_unpaired_disposal');
      });
    }
    if (acquisitions.length > 0) {
      stats.unpairedAcquisitionFromBridge = acquisitions.length;
      const aset = new Set(acquisitions);
      out = out.map((e) => {
        if (!aset.has(e)) return e;
        stats.reviewFlagsAdded++;
        return withReviewFlag(e, 'cronos_bridge_unpaired_acquisition');
      });
    }
    return { events: out, stats };
  }

  const acqSorted = [...acquisitions].sort((a, b) => a.ts - b.ts);
  const usedAcq = new Set<LedgerEvent>();
  const matches: Match[] = [];

  const disSorted = [...disposals].sort((a, b) => a.ts - b.ts);

  for (const d of disSorted) {
    const dTo = normAddr(d.toAddress)!;
    let best: { ev: LedgerEvent; score: number } | null = null;

    for (const a of acqSorted) {
      if (usedAcq.has(a)) continue;
      const aFrom = normAddr(a.fromAddress)!;
      if (aFrom !== dTo) continue;
      if (!amountsMatch(d.amount, a.amount)) continue;
      if (a.ts < d.ts) continue;
      if (a.ts - d.ts > MAX_BRIDGE_LEG_GAP_MS) continue;
      const score = a.ts - d.ts;
      if (best == null || score < best.score) {
        best = { ev: a, score };
      }
    }

    if (best != null) {
      usedAcq.add(best.ev);
      matches.push({ disposal: d, acquisition: best.ev });
    }
  }

  const matchedDisposals = new Set(matches.map((m) => m.disposal));
  const matchedAcquisitions = new Set(matches.map((m) => m.acquisition));

  const stats = emptyStats();
  stats.pairsMatched = matches.length;
  for (const m of matches) {
    stats.croAmountBridged = stats.croAmountBridged.plus(m.disposal.amount);
  }

  const synthetic: LedgerEvent[] = [];
  const remove = new Set<LedgerEvent>();
  for (const m of matches) {
    remove.add(m.disposal);
    remove.add(m.acquisition);
    stats.disposalLegsRemoved++;
    stats.acquisitionLegsRemoved++;

    const wOut = normAddr(m.disposal.fromAddress)!;
    const wIn = normAddr(m.acquisition.toAddress)!;

    if (wOut === wIn) {
      stats.sameWalletPairsSuppressed++;
      continue;
    }

    const refD = m.disposal.ref ?? '';
    const refA = m.acquisition.ref ?? '';
    synthetic.push({
      ts: m.acquisition.ts,
      source: 'cronos',
      kind: 'internal_transfer',
      asset: m.disposal.asset,
      amount: m.disposal.amount,
      eurValue: null,
      ref: `bridge_cont_${refD || 'noref'}_${refA || 'noref'}`,
      classification: 'INTERNAL_TRANSFER',
      fiTaxCategory: 'NON_TAXABLE_INTERNAL',
      fiSubreason: CRONOS_BRIDGE_FI_SUBREASON,
      explanation:
        'Matched Cronos CRO outbound to bridge contract with a later inbound from the same bridge — cross-chain continuity (not a taxable disposal or fresh acquisition).',
      fromAddress: wOut,
      toAddress: wIn,
      primaryWalletAddress: wIn,
      reviewFlags: ['cronos_pos_evm_bridge_continuity', 'cross_chain_internal'],
      basisContinuityRef: refD || refA || undefined,
    });
    stats.syntheticInternalInserted++;
  }

  for (const d of disposals) {
    if (matchedDisposals.has(d)) continue;
    stats.unpairedDisposalToBridge++;
  }
  for (const a of acquisitions) {
    if (matchedAcquisitions.has(a)) continue;
    stats.unpairedAcquisitionFromBridge++;
  }

  const unmatchedDisposalSet = new Set(
    disposals.filter((d) => !matchedDisposals.has(d))
  );
  const unmatchedAcquisitionSet = new Set(
    acquisitions.filter((a) => !matchedAcquisitions.has(a))
  );

  let out: LedgerEvent[] = events
    .filter((e) => !remove.has(e))
    .map((e) => {
      if (unmatchedDisposalSet.has(e)) {
        stats.reviewFlagsAdded++;
        return withReviewFlag(e, 'cronos_bridge_unpaired_disposal');
      }
      if (unmatchedAcquisitionSet.has(e)) {
        stats.reviewFlagsAdded++;
        return withReviewFlag(e, 'cronos_bridge_unpaired_acquisition');
      }
      return e;
    });

  out.push(...synthetic);
  out.sort((a, b) => {
    if (a.ts !== b.ts) return a.ts - b.ts;
    return String(a.ref ?? '').localeCompare(String(b.ref ?? ''));
  });

  return { events: out, stats };
}

export function buildCronosBridgeDiagnosticsCsv(stats: CronosBridgeContinuityStats): string {
  const rows: string[][] = [
    ['pairs_matched', String(stats.pairsMatched)],
    ['disposal_legs_removed', String(stats.disposalLegsRemoved)],
    ['acquisition_legs_removed', String(stats.acquisitionLegsRemoved)],
    ['synthetic_internal_inserted', String(stats.syntheticInternalInserted)],
    ['same_wallet_pairs_suppressed', String(stats.sameWalletPairsSuppressed)],
    ['cro_amount_bridged', stats.croAmountBridged.toFixed(8)],
    ['unpaired_disposal_to_bridge', String(stats.unpairedDisposalToBridge)],
    ['unpaired_acquisition_from_bridge', String(stats.unpairedAcquisitionFromBridge)],
    ['review_flags_added', String(stats.reviewFlagsAdded)],
  ];
  return buildMetricValueCsv(rows);
}
